/**
 * 用户详情页面
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Descriptions,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Table,
  Typography,
  Spin,
  DatePicker,
  Radio,
  Popconfirm,
  Alert,
  Result,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  ReloadOutlined,
  StopOutlined,
  CheckCircleOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { getUserDetail, updateUser, resetUserLimit, UserDetail, getUserPurchases, PurchaseEvent, adjustUserCredits, CreditLedgerRow } from '../api/users';
import dayjs from 'dayjs';
import { formatBeijingTime, formatBeijingDate } from '../utils/time';

const { Title, Text } = Typography;
const { TextArea } = Input;

const UserDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { device_id } = useParams<{ device_id: string }>();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<UserDetail | null>(null);
  const [purchases, setPurchases] = useState<PurchaseEvent[]>([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [blockType, setBlockType] = useState<'none' | 'permanent' | 'temporary'>('none');
  const [form] = Form.useForm();
  const [adjustForm] = Form.useForm();
  const [adjustModalVisible, setAdjustModalVisible] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  /**
   * 加载用户详情
   */
  const loadUserDetail = async () => {
    if (!device_id) return;
    
    setLoading(true);
    setLoadError(null);
    setUser(null);
    try {
      const response = await getUserDetail(device_id);
      if (response.success) {
        const payload: any = response.data;
        const base = payload.user ?? payload;
        setUser({
          ...base,
          monthly_stats: payload.monthly_stats ?? base.monthly_stats ?? [],
          recent_requests: payload.recent_requests ?? base.recent_requests ?? [],
          credits: payload.credits ?? base.credits,
          credits_preview: payload.credits_preview ?? base.credits_preview,
          credit_seed: payload.credit_seed ?? base.credit_seed,
          credit_ledger: payload.credit_ledger ?? base.credit_ledger ?? [],
          profile_source: payload.profile_source ?? base.profile_source ?? 'api_user',
        });
      } else {
        setLoadError((response as any).errors?.[0]?.message || '用户不存在');
      }
    } catch (error: any) {
      console.error('加载用户详情失败:', error);
      setLoadError(error?.response?.data?.errors?.[0]?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 加载购买历史
   */
  const loadPurchases = async () => {
    if (!device_id) return;
    
    setPurchasesLoading(true);
    try {
      const response = await getUserPurchases(device_id);
      if (response.success) {
        setPurchases(response.data.purchases);
      }
    } catch (error) {
      console.error('加载购买历史失败:', error);
    } finally {
      setPurchasesLoading(false);
    }
  };

  useEffect(() => {
    loadUserDetail();
    loadPurchases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device_id]);

  /**
   * 打开编辑弹窗
   */
  const handleEdit = () => {
    if (!user) return;
    
    // 判断封禁类型
    let type: 'none' | 'permanent' | 'temporary' = 'none';
    if (user.is_blocked) {
      type = user.blocked_until ? 'temporary' : 'permanent';
    }
    setBlockType(type);
    
    form.setFieldsValue({
      custom_daily_limit: user.custom_daily_limit,
      notes: user.notes,
      blocked_until: user.blocked_until ? dayjs(user.blocked_until) : null,
    });
    setEditModalVisible(true);
  };

  /**
   * 保存编辑
   */
  const handleSaveEdit = async () => {
    if (!device_id) return;
    
    try {
      const values = await form.validateFields();
      
      // 根据封禁类型构建请求数据
      const updateData: any = {
        custom_daily_limit: values.custom_daily_limit,
        notes: values.notes,
      };
      
      if (blockType === 'none') {
        // 不封禁
        updateData.is_blocked = false;
        updateData.blocked_until = null;
      } else if (blockType === 'permanent') {
        // 永久封禁
        updateData.is_blocked = true;
        updateData.blocked_until = null;
      } else if (blockType === 'temporary') {
        // 临时封禁
        updateData.is_blocked = true;
        updateData.blocked_until = values.blocked_until ? values.blocked_until.toISOString() : null;
      }
      
      await updateUser(device_id, updateData);
      message.success('更新成功');
      setEditModalVisible(false);
      loadUserDetail();
    } catch (error) {
      console.error('更新失败:', error);
      message.error('更新失败');
    }
  };

  /**
   * 重置限额
   */
  const handleResetLimit = async () => {
    if (!device_id) return;
    
    try {
      await resetUserLimit(device_id);
      message.success('限额已重置');
      loadUserDetail();
    } catch (error) {
      console.error('重置失败:', error);
      message.error('重置失败');
    }
  };

  const handleAdjustCredits = async () => {
    if (!device_id) return;
    try {
      const values = await adjustForm.validateFields();
      setAdjusting(true);
      const amount = Number(values.amount);
      const idempotencyKey = `adjust:${device_id}:${Date.now()}`;
      await adjustUserCredits(device_id, {
        amount,
        reason: values.reason,
        idempotency_key: idempotencyKey,
      });
      message.success('积分已调整。老包未升级看不到这笔账本积分。');
      setAdjustModalVisible(false);
      adjustForm.resetFields();
      loadUserDetail();
    } catch (error) {
      console.error('调整积分失败:', error);
      message.error('调整积分失败');
    } finally {
      setAdjusting(false);
    }
  };

  /**
   * 购买历史表格列
   */
  const purchaseColumns: ColumnsType<PurchaseEvent> = [
    {
      title: '购买时间',
      dataIndex: 'purchase_date',
      key: 'purchase_date',
      width: 180,
      render: (time: string) => formatBeijingTime(time),
    },
    {
      title: '产品ID',
      dataIndex: 'product_id',
      key: 'product_id',
      ellipsis: true,
      render: (id: string) => {
        let name = id;
        if (id.includes('weekly')) name = '周订阅';
        else if (id.includes('monthly')) name = '月订阅';
        else if (id.includes('package')) name = '积分包';
        return <Text copyable={{ text: id }}>{name}</Text>;
      },
    },
    {
      title: '类型',
      dataIndex: 'product_type',
      key: 'product_type',
      width: 100,
      render: (type: string) => {
        const color = type === 'subscription' ? 'blue' : 'orange';
        const text = type === 'subscription' ? '订阅' : '消耗品';
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '数量',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (qty: number) => <Tag color="green">{qty} 次</Tag>,
    },
    {
      title: '到期时间',
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      width: 180,
      render: (time: string | null) => {
        if (!time) return '-';
        const expiry = dayjs(time);
        const isExpired = expiry.isBefore(dayjs());
        return (
          <Text type={isExpired ? 'secondary' : undefined}>
            {expiry.format('YYYY-MM-DD HH:mm:ss')}
            {isExpired && ' (已过期)'}
          </Text>
        );
      },
    },
    {
      title: '交易ID',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      width: 120,
      ellipsis: true,
      render: (id: string) => (
        <Text copyable={{ text: id }} style={{ fontSize: 12, fontFamily: 'monospace' }}>
          {id.substring(0, 12)}...
        </Text>
      ),
    },
  ];

  /**
   * 请求历史表格列
   */
  const requestColumns: ColumnsType<any> = [
    {
      title: '时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (time: string) => formatBeijingTime(time),
    },
    {
      title: '方法',
      dataIndex: 'method',
      key: 'method',
      width: 80,
      render: (method: string) => <Tag color="blue">{method}</Tag>,
    },
    {
      title: '端点',
      dataIndex: 'endpoint',
      key: 'endpoint',
      ellipsis: true,
    },
    {
      title: '状态码',
      dataIndex: 'status_code',
      key: 'status_code',
      width: 100,
      render: (code: number | null) => {
        if (!code) return '-';
        const color = code >= 200 && code < 300 ? 'green' : code >= 400 ? 'red' : 'orange';
        return <Tag color={color}>{code}</Tag>;
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (loadError || !user) {
    return (
      <div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>
          返回
        </Button>
        <Result
          status="warning"
          title={loadError || '用户不存在'}
          subTitle="没有 api_users 记录，也没有积分账本或安装上报。"
        />
      </div>
    );
  }

  const hasApiUser = user.profile_source !== 'credit_only' && user.profile_source !== 'install_only';

  // 计算最近2天的请求数
  const now = new Date();
  const todayKey = now.toISOString().slice(0, 10);
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const yesterdayKey = yesterday.toISOString().slice(0, 10);
  const recentRequests = user.monthly_stats
    .filter(s => s.month_key === todayKey || s.month_key === yesterdayKey)
    .reduce((sum, s) => sum + s.request_count, 0);
  const effectiveLimit = user.effective_limit || user.custom_daily_limit || 100;

  return (
    <div>
      {/* 页面标题和操作 */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/users')}>
            返回
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            用户详情
          </Title>
        </Space>
        <Space>
          <Button icon={<EditOutlined />} onClick={handleEdit} disabled={!hasApiUser}>
            编辑
          </Button>
          <Button icon={<ReloadOutlined />} onClick={loadUserDetail}>
            刷新
          </Button>
        </Space>
      </div>

      {!hasApiUser ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="该设备还没有生成记录"
          description="详情来自积分账本和安装上报，不是用户表。封禁/备注不可用，积分调整可用。"
        />
      ) : null}

      {/* 基本信息 */}
      <Card title="基本信息" style={{ marginBottom: 16 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label="User ID">
            {user.support_id || user.credits?.support_id ? (
              <Text copyable style={{ fontFamily: 'monospace' }}>
                {user.support_id || user.credits?.support_id}
              </Text>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Device ID">
            <Text copyable style={{ fontFamily: 'monospace' }}>
              {user.device_id}
            </Text>
          </Descriptions.Item>
          {user.server_fingerprint && (
            <Descriptions.Item label="服务端指纹" span={2}>
              <Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>
                {user.server_fingerprint}
              </Text>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="首次访问">
            {formatBeijingTime(user.first_seen_at)}
          </Descriptions.Item>
          <Descriptions.Item label="最后访问">
            {formatBeijingTime(user.last_seen_at)}
          </Descriptions.Item>
          <Descriptions.Item label="App 版本">
            {user.app_version || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="设备型号">
            {user.device_model || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="系统版本">
            {user.system_version || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="IP地址">
            {user.last_ip || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="位置" span={2}>
            {[user.city, user.region, user.country].filter(Boolean).join(', ') || '-'}
          </Descriptions.Item>
          {user.latitude && user.longitude && (
            <Descriptions.Item label="坐标" span={2}>
              <a 
                href={`https://www.google.com/maps?q=${user.latitude},${user.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {user.latitude}, {user.longitude} (在地图上查看)
              </a>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="状态">
            {user.is_blocked ? (
              <Space direction="vertical" size="small">
                <Tag color="red" icon={<StopOutlined />}>
                  {user.blocked_until ? '临时封禁' : '永久封禁'}
                </Tag>
                {user.blocked_until && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    解封时间: {formatBeijingTime(user.blocked_until)}
                  </Text>
                )}
              </Space>
            ) : (
              <Tag color="green" icon={<CheckCircleOutlined />}>
                正常
              </Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="总请求数">
            {user.total_requests}
          </Descriptions.Item>
          <Descriptions.Item label="备注" span={2}>
            {user.notes || '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 限流状态 */}
      <Card 
        title="限流状态" 
        style={{ marginBottom: 16 }}
        extra={
          <Popconfirm
            title="确认重置限额"
            description="确定要重置该用户今日的请求限额吗？"
            onConfirm={handleResetLimit}
            okText="确定"
            cancelText="取消"
          >
            <Button size="small" disabled={!hasApiUser}>重置今日限额</Button>
          </Popconfirm>
        }
      >
        <Descriptions column={2} bordered>
          <Descriptions.Item label="近2天请求数">
            <Tag color={recentRequests >= effectiveLimit ? 'red' : 'green'}>
              {recentRequests} / {effectiveLimit}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="自定义限额">
            {user.custom_daily_limit ? `${user.custom_daily_limit} 次/2天` : `使用默认值 (${effectiveLimit}次/2天)`}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 积分账本 */}
      <Card
        title="积分账本"
        style={{ marginBottom: 16 }}
        extra={
          <Button size="small" onClick={() => setAdjustModalVisible(true)}>
            调整积分
          </Button>
        }
      >
        <Descriptions column={2} bordered>
          <Descriptions.Item label="客户端上报">
            <Tag color="default">{user.credits_preview?.client_quota ?? user.subscription_quota ?? 0}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="重算值">
            <Tag color="blue">{user.credits_preview?.reconstructed ?? 0}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="账本可用">
            <Tag color={(user.credits?.available || 0) > 0 ? 'green' : 'default'} style={{ fontSize: 16 }}>
              {user.credits?.available ?? 0}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="占用中">
            {user.credits?.held ?? 0}
          </Descriptions.Item>
          <Descriptions.Item label="累计入账">
            {user.credits?.lifetime_granted ?? 0}
          </Descriptions.Item>
          <Descriptions.Item label="累计消耗">
            {user.credits?.lifetime_spent ?? 0}
          </Descriptions.Item>
          <Descriptions.Item label="历史购买积分">
            {user.credits_preview?.purchased ?? 0}
          </Descriptions.Item>
          <Descriptions.Item label="历史生成次数">
            {user.credits_preview?.generate_count ?? 0}
          </Descriptions.Item>
          <Descriptions.Item label="Seed 申报">
            {user.credit_seed?.seed_reported ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Seed 写入">
            {user.credit_seed?.seed_applied ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Seed 时购买折合">
            {user.credit_seed?.seed_purchase_sum ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label="积分异常">
            {user.credit_seed?.is_anomaly ? (
              <Tag color="orange">{(user.credit_seed?.anomaly_flags || []).join(', ') || 'flagged'}</Tag>
            ) : (
              <Tag>正常</Tag>
            )}
          </Descriptions.Item>
        </Descriptions>
        <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
          老包仍使用本地 Keychain，后台加减只对已升级新包的设备生效。重算值 = 购买 + 2 次试用 − 生成次数，仅供对照，不会自动改账。Seed 异常仅供人工关注，不会自动扣分。
        </Text>
        <Table
          style={{ marginTop: 16 }}
          size="small"
          rowKey="id"
          pagination={{ pageSize: 8 }}
          dataSource={user.credit_ledger || []}
          columns={[
            {
              title: '时间',
              dataIndex: 'created_at',
              width: 180,
              render: (time: string) => time ? formatBeijingTime(time) : '-',
            },
            { title: '类型', dataIndex: 'entry_type', width: 110 },
            {
              title: '数量',
              dataIndex: 'amount',
              width: 80,
              render: (amount: number) => (
                <Tag color={amount > 0 ? 'green' : amount < 0 ? 'red' : 'default'}>
                  {amount > 0 ? `+${amount}` : amount}
                </Tag>
              ),
            },
            { title: '之后可用', dataIndex: 'available_after', width: 90 },
            { title: '来源', dataIndex: 'source', width: 90 },
            { title: '原因', dataIndex: 'reason', ellipsis: true },
          ] as ColumnsType<CreditLedgerRow>}
          locale={{ emptyText: '暂无账本流水' }}
        />
      </Card>

      {/* 订阅信息 */}
      <Card title="订阅信息" style={{ marginBottom: 16 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label="订阅积分">
            <Tag color={user.subscription_quota > 0 ? 'blue' : 'default'} style={{ fontSize: 16 }}>
              {user.subscription_quota} 次
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="订阅类型">
            {user.subscription_type === 'weekly' && <Tag color="purple">周订阅</Tag>}
            {user.subscription_type === 'monthly' && <Tag color="blue">月订阅</Tag>}
            {(!user.subscription_type || user.subscription_type === 'none') && <Tag color="default">未订阅</Tag>}
          </Descriptions.Item>
        </Descriptions>

        {/* 历史月份统计 */}
        <div style={{ marginTop: 16 }}>
          <Text strong>历史月份统计：</Text>
          <div style={{ marginTop: 8 }}>
            {user.monthly_stats.map((stat) => (
              <Tag key={stat.month_key} style={{ marginBottom: 8 }}>
                {stat.month_key}: {stat.request_count} 次
              </Tag>
            ))}
          </div>
        </div>
      </Card>

      {/* 购买历史 */}
      <Card 
        title={
          <Space>
            <ShoppingOutlined />
            <span>购买历史（共 {purchases.length} 条）</span>
          </Space>
        }
        style={{ marginBottom: 16 }}
        extra={<Button size="small" icon={<ReloadOutlined />} onClick={loadPurchases}>刷新</Button>}
      >
        <Table
          columns={purchaseColumns}
          dataSource={purchases}
          rowKey="id"
          loading={purchasesLoading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1000 }}
          locale={{ emptyText: '暂无购买记录' }}
        />
      </Card>

      {/* 请求历史 */}
      <Card title={`请求历史（最近 ${user.recent_requests.length} 条）`}>
        <Table
          columns={requestColumns}
          dataSource={user.recent_requests}
          rowKey={(record, index) => `${record.timestamp}-${index}`}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 800 }}
        />
      </Card>

      <Modal
        title="调整积分"
        open={adjustModalVisible}
        onOk={handleAdjustCredits}
        onCancel={() => setAdjustModalVisible(false)}
        confirmLoading={adjusting}
        okText="确认调整"
        cancelText="取消"
      >
        <Form form={adjustForm} layout="vertical">
          <Form.Item
            label="数量（正数为补发，负数为扣除）"
            name="amount"
            rules={[{ required: true, message: '请输入非 0 整数' }]}
          >
            <InputNumber style={{ width: '100%' }} precision={0} />
          </Form.Item>
          <Form.Item
            label="原因"
            name="reason"
            rules={[{ required: true, message: '请填写原因' }]}
          >
            <TextArea rows={3} placeholder="例如：restore James Oct 11 Keychain loss, txn 520003061717152" />
          </Form.Item>
          <Text type="warning">
            不会直接改余额，只会追加一笔账本流水。老包用户需要先升级才能看到。
          </Text>
        </Form>
      </Modal>

      {/* 编辑弹窗 */}
      <Modal
        title="编辑用户信息"
        open={editModalVisible}
        onOk={handleSaveEdit}
        onCancel={() => setEditModalVisible(false)}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item label="封禁状态">
            <Radio.Group value={blockType} onChange={(e) => setBlockType(e.target.value)}>
              <Space direction="vertical">
                <Radio value="none">正常（不封禁）</Radio>
                <Radio value="permanent">永久封禁</Radio>
                <Radio value="temporary">临时封禁</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          
          {blockType === 'temporary' && (
            <Form.Item 
              label="封禁到期时间" 
              name="blocked_until"
              rules={[{ required: true, message: '请选择封禁到期时间' }]}
            >
              <DatePicker 
                showTime 
                style={{ width: '100%' }} 
                placeholder="选择解封时间"
                disabledDate={(current) => current && current < dayjs().startOf('day')}
              />
            </Form.Item>
          )}
          
          <Form.Item 
            label="自定义每日限额" 
            name="custom_daily_limit"
            help="留空使用默认值 (100次/2天)"
          >
            <InputNumber min={0} max={1000} style={{ width: '100%' }} placeholder="50" />
          </Form.Item>
          <Form.Item label="备注" name="notes">
            <TextArea rows={4} placeholder="添加备注信息..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserDetailPage;
